import React, {useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../forecast-app/shared/components/modals/types';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {CommitMutation} from '../../../mutations/CommitMutation';
import {BASELINE_TEMPLATES_PATH} from '../../../forecast-app/admin-tab/templates-page/templatesPaths';
import InsertBaselineTemplateMutation from './InsertBaselineTemplateMutation';
import {InsertTemplateList} from './InsertTemplateList';
import {InsertTemplatePreview} from './InsertTemplatePreview';
import {InsertTemplateConfirm} from './InsertTemplateConfirm';

export type InsertBaselineTemplateModalPayload = {
	/** The project's Relay global id (`project.id`), not its company project id. */
	projectId: string;
	/** The project has a phase, or a deliverable while it uses deliverables, so an insert replaces them. */
	hasContent: boolean;
	projectUsesDeliverables: boolean;
	/** Called after a successful insert, once the modal has closed, to reload the Baseline page. */
	onInserted: () => void;
};

export const showInsertBaselineTemplateModal = (props: InsertBaselineTemplateModalPayload): void => {
	showModal({type: MODAL_TYPE.INSERT_BASELINE_TEMPLATE, ...props});
};

type Picked = {id: string; name: string | null};
type View =
	| {kind: 'list'}
	| {kind: 'preview'; templateId: string}
	| {kind: 'confirm'; template: Picked; from: 'list' | 'preview'};

export const InsertBaselineTemplateModal = ({
	projectId,
	hasContent,
	projectUsesDeliverables,
	onInserted,
	closeModal,
}: ModalProps<InsertBaselineTemplateModalPayload>): JSX.Element => {
	const history = useHistory();
	const [view, setView] = useState<View>({kind: 'list'});
	// Lives here rather than in the list, so it survives a trip to a preview and back.
	const [query, setQuery] = useState('');
	const [submitting, setSubmitting] = useState(false);
	// Set once this modal has been closed while an insert is in flight: by the user (Escape or the close
	// button), or by a failed insert. The insert's outcome must then not close the modal a second time,
	// which would pop whatever modal is open underneath.
	const closed = useRef(false);
	const closeOnce = () => {
		if (!closed.current) {
			closed.current = true;
			closeModal();
		}
	};
	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);

	const manageTemplates = () => {
		closeModal();
		history.push(BASELINE_TEMPLATES_PATH);
	};

	const insert = (template: Picked) => {
		setSubmitting(true);
		CommitMutation(
			InsertBaselineTemplateMutation,
			{projectId, baselineTemplateId: template.id},
			() => {
				closeOnce();
				onInserted();
			},
			// No automatic retry: an insert is not idempotent, so a resend after a lost response would count
			// usage twice or be refused because the first one already filled the Baseline.
			false,
			null,
			(error, onFailure) => {
				// The shared error modals open beneath this design-system modal, so it closes first or the
				// error could not be seen. Nothing was saved, so the user can simply open it again.
				closeOnce();
				onFailure?.(error);
			}
		);
	};

	if (view.kind === 'preview') {
		return (
			<InsertTemplatePreview
				projectId={projectId}
				templateId={view.templateId}
				projectUsesDeliverables={projectUsesDeliverables}
				onBack={() => setView({kind: 'list'})}
				onInsert={template => setView({kind: 'confirm', template, from: 'preview'})}
				onCancel={closeModal}
			/>
		);
	}
	if (view.kind === 'confirm') {
		// The design-system modal cannot refuse to close, so closing it mid-insert closes the whole
		// modal rather than going back to the list, where a second insert could be started.
		const cancelConfirm = () => {
			if (submitting) {
				closeOnce();
				return;
			}
			setView(view.from === 'preview' ? {kind: 'preview', templateId: view.template.id} : {kind: 'list'});
		};
		return (
			<InsertTemplateConfirm
				templateName={view.template.name ?? ''}
				hasContent={hasContent}
				projectUsesDeliverables={projectUsesDeliverables}
				submitting={submitting}
				onConfirm={() => insert(view.template)}
				onCancel={cancelConfirm}
			/>
		);
	}
	return (
		<InsertTemplateList
			projectId={projectId}
			query={query}
			isAdmin={isAdmin}
			projectUsesDeliverables={projectUsesDeliverables}
			onQueryChange={setQuery}
			onPreview={template => setView({kind: 'preview', templateId: template.id})}
			onInsert={template => setView({kind: 'confirm', template, from: 'list'})}
			onManage={manageTemplates}
			onCancel={closeModal}
		/>
	);
};
